import { apiFetch } from '../lib/apiFetch';
import { asCount } from '../utils/counts';

import type { PostingCadence, SocialPlatform, VenueDetail } from './venues';

export type NoteKind = 'RD_NOTE' | 'MARKETING_NOTE' | 'ACTION_ITEM';

export interface MarketingNote {
  id: string;
  venueId: string;
  kind: NoteKind;
  body: string;
  isDone: boolean;
  doneAt: string | null;
  doneByName: string | null;
  authorId: string | null;
  authorName: string;
  source: 'MANUAL' | 'AUDIT_IMPORT';
  /** Whether the caller may change the text or delete the note. */
  canEdit: boolean;
  editedAt: string | null;
  editedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingNotesPage {
  notes: MarketingNote[];
  page: number;
  pageSize: number;
  total: number;
  /** Open action items for the whole venue, whatever filter is in use. */
  openActionItemCount: number;
}

export type MarketingNotesQuery = { page: number } & (
  | { view: 'history' }
  | { kind: NoteKind; status: 'open' | 'done' }
);

/** A platform that is sent needs a handle. Absent audienceSize or cadence keeps
 *  the current value and null clears it. */
export interface PlatformPatch {
  handle: string;
  audienceSize?: number | null;
  cadence?: PostingCadence | null;
}

export interface VenueMarketingPatch {
  social?: Partial<Record<SocialPlatform, PlatformPatch | null>> & { website?: string | null };
  markVerified?: SocialPlatform[];
  comarketingRating?: number | null;
  followingOnInstagram?: boolean | null;
  hubspotUrl?: string | null;
}

export type MarketingNotePatch = { body?: string; isDone?: boolean };

const PAGE_SIZE = 20;

/** A refused marketing request. `status` tells the caller why; `fieldErrors`
 *  keys are dotted paths such as `social.instagram.handle`, or `_` for the form. */
export class MarketingApiError extends Error {
  status: number;
  fieldErrors: Record<string, string>;
  constructor(status: number, message: string, fieldErrors: Record<string, string> = {}) {
    super(message);
    this.name = 'MarketingApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

async function failure(res: Response): Promise<MarketingApiError> {
  const body = (await res.json().catch(() => ({}))) as {
    message?: string;
    fieldErrors?: Record<string, string>;
  };
  return new MarketingApiError(
    res.status,
    body.message ?? `Request failed: ${res.status}`,
    body.fieldErrors ?? {},
  );
}

async function send(url: string, method: string, body?: unknown): Promise<Response> {
  const res = await apiFetch(url, {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!res.ok) throw await failure(res);
  return res;
}

const venueUrl = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}`;
const noteUrl = (venueId: string, noteId: string) =>
  `${venueUrl(venueId)}/marketing-notes/${encodeURIComponent(noteId)}`;

export async function patchVenueMarketing(
  venueId: string,
  patch: VenueMarketingPatch,
): Promise<VenueDetail> {
  const res = await send(`${venueUrl(venueId)}/marketing`, 'PATCH', patch);
  return ((await res.json()) as { venue: VenueDetail }).venue;
}

export async function fetchMarketingNotes(
  venueId: string,
  query: MarketingNotesQuery,
): Promise<MarketingNotesPage> {
  const params = new URLSearchParams();
  if ('view' in query) {
    params.set('view', query.view);
  } else {
    params.set('kind', query.kind);
    params.set('status', query.status);
  }
  params.set('page', String(query.page));
  params.set('pageSize', String(PAGE_SIZE));
  const res = await send(`${venueUrl(venueId)}/marketing-notes?${params}`, 'GET');
  return (await res.json()) as MarketingNotesPage;
}

/** Open action items on active venues in the caller's assigned regions (sub-regions included). */
export async function fetchOpenTaskCount(): Promise<number> {
  const res = await send('/api/v1/venues/action-items/count', 'GET');
  const { openCount } = (await res.json()) as { openCount?: unknown };
  // A missing or odd value is 0, so an older backend never puts "undefined" on screen.
  return asCount(openCount);
}

export async function createMarketingNote(
  venueId: string,
  input: { kind: NoteKind; body: string },
): Promise<MarketingNote> {
  const res = await send(`${venueUrl(venueId)}/marketing-notes`, 'POST', input);
  return ((await res.json()) as { note: MarketingNote }).note;
}

export async function updateMarketingNote(
  venueId: string,
  noteId: string,
  patch: MarketingNotePatch,
): Promise<MarketingNote> {
  const res = await send(noteUrl(venueId, noteId), 'PATCH', patch);
  return ((await res.json()) as { note: MarketingNote }).note;
}

export async function deleteMarketingNote(venueId: string, noteId: string): Promise<void> {
  await send(noteUrl(venueId, noteId), 'DELETE');
}
